import { MoreHorizontal } from "lucide-react";
import * as React from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TabsList, TabsTrigger } from "@/components/ui/misc";
import { cn } from "@/lib/utils";

export interface OverflowTab {
  value: string;
  label: React.ReactNode;
}

/** The ⋯ button's width until it has been drawn: the icon and the tab's own padding. */
const MORE_WIDTH = 20;

/**
 * A tab row for a narrow panel (ACL-590): the tabs that fit, in their order, and the rest under a ⋯
 * button at the end of the row. The open tab is always in the row: one picked from under ⋯ takes the
 * last place there, and the tab it pushed out goes under ⋯ until another is picked.
 *
 * Each tab is measured while it is in the row, and every tab is in the row on the first render, so a
 * tab under ⋯ keeps the width it had. Until the row has a width (the first layout, a test's DOM) every
 * tab is shown.
 */
export function OverflowTabsList({
  tabs, value, onSelect, className,
}: {
  tabs: readonly OverflowTab[];
  value: string;
  onSelect: (value: string) => void;
  className?: string;
}) {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const moreRef = React.useRef<HTMLButtonElement>(null);
  const widths = React.useRef(new Map<string, number>());
  const moreWidth = React.useRef(MORE_WIDTH);
  /** The tabs in the row, in order; null is every tab. */
  const [inRow, setInRow] = React.useState<readonly string[] | null>(null);

  const fit = () => {
    const row = rowRef.current;
    if (!row) return;
    for (const el of row.querySelectorAll<HTMLElement>("[data-tab]")) {
      widths.current.set(el.dataset.tab!, el.getBoundingClientRect().width);
    }
    if (moreRef.current) moreWidth.current = moreRef.current.getBoundingClientRect().width;
    const style = getComputedStyle(row);
    const room = row.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const gap = parseFloat(style.columnGap) || 0;
    const width = (values: readonly string[]) =>
      values.reduce((sum, v) => sum + (widths.current.get(v) ?? 0), 0) + gap * Math.max(values.length - 1, 0);
    const all = tabs.map((t) => t.value);
    let next: readonly string[] | null = null;
    if (room > 0 && width(all) > room) {
      const fits = (values: readonly string[]) => width(values) + gap + moreWidth.current <= room;
      let k = all.length - 1;
      while (k > 1 && !fits(all.slice(0, k))) k -= 1;
      next = all.slice(0, k);
      if (!next.includes(value) && all.includes(value)) {
        // The open tab takes the last places it needs.
        let j = k - 1;
        while (j > 0 && !fits([...all.slice(0, j), value])) j -= 1;
        next = [...all.slice(0, j), value];
      }
    }
    setInRow((was) => (was?.join() === next?.join() ? was : next));
  };
  // After every render (a tab's name may have changed, or the open tab), and whenever the row is resized.
  const latest = React.useRef(fit);
  latest.current = fit;
  React.useLayoutEffect(() => latest.current());
  React.useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(() => latest.current());
    watch.observe(row);
    return () => watch.disconnect();
  }, []);

  const shown = inRow ? tabs.filter((t) => inRow.includes(t.value)) : tabs;
  const under = inRow ? tabs.filter((t) => !inRow.includes(t.value)) : [];
  return (
    // The row draws the line under the tabs, so the ⋯ button sits on it as a tab does.
    <div ref={rowRef} className={cn("flex items-end gap-4.5 border-b border-border", className)}>
      <TabsList className="min-w-0 gap-4.5 border-b-0">
        {shown.map((t) => (
          <TabsTrigger
            key={t.value}
            value={t.value}
            data-tab={t.value}
            className="text-base text-gray-11 data-[state=active]:text-accent-11"
          >
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {under.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              ref={moreRef}
              type="button"
              aria-label="More tabs"
              title="More tabs"
              className="relative -mb-px ml-auto inline-flex items-center border-b-2 border-transparent px-0.5 pb-2 pt-1 text-gray-11 transition-colors hover:text-text data-[state=open]:text-text"
            >
              <MoreHorizontal className="size-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            {under.map((t) => (
              <DropdownMenuItem key={t.value} onSelect={() => onSelect(t.value)}>
                {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
